import AdUnit from "../../core/AdUnit";
import {
  injectScript,
  getRandomString,
  createVideoSlot,
  convertBufferToString,
} from "../../utils/utils";
import { GOOGLE_IMA_URL, AdvIDPrefix, AdUnitStatus, event } from "../../utils/const";
import LibLoader from "../../utils/LibLoader";

class GoogleIMA extends AdUnit {
  constructor(cfg) {
    super(cfg);

    this.controller = null;
    this.adDisplayContainer = null;
    this.adsLoader = null;
    this.adsManager = null;

    this._init();
  }

  _init() {
    if (!(window.google && window.google.ima)) {
      return LibLoader.load(GOOGLE_IMA_URL,
        () => { this._init(); },
        () => {
          this.localEmitter.emit(event.Ad.AdError, { code: 9 });
          this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });
        }
      );
    }

    this.logger.log(`[GoogleIMA - ${this.config.playerConfig.zone} / ${this.config.params.tid}] _init() called`, this.config);

    this.advID = AdvIDPrefix.GOOGLE + getRandomString();
    this.controller = window.google.ima;

    const { contentVideoSlot } = this.config;

    this.videoSlot = createVideoSlot();
    this.slot.appendChild(this.videoSlot);
    this.slot.onclick = () => this.emitter.emit(event.Player.UserInteractedWithUI);

    this.config.playerNode.appendChild(this.slot);

    this.controller.settings.setDisableCustomPlaybackForIOS10Plus(true);
    this.controller.settings.setPlayerType('flyads/html5');
    this.adDisplayContainer = new this.controller.AdDisplayContainer(
      this.slot,
      contentVideoSlot
    ); // videoSlot
    this.adsLoader = new this.controller.AdsLoader(this.adDisplayContainer);

    this.adDisplayContainer.initialize();

    if (this.adsLoader) {
      const { AdsManagerLoadedEvent } = this.controller;

      this.adsLoader.addEventListener(this.controller.AdErrorEvent.Type.AD_ERROR, ({ error }) => {
        console.log('error 1: ', error);
        this.localEmitter.emit(event.Ad.AdError, { code: error?.data?.errorCode === 303 ? 1 : 5});
        this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });
      }, false);
      this.adsLoader.addEventListener(AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (evt) => {
          const adsRenderingSettings = new this.controller.AdsRenderingSettings();
          adsRenderingSettings.loadVideoTimeout = this.config.timeouts.googleImaInit;
          adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = false;
          adsRenderingSettings.useStyledNonLinearAds = true;
          adsRenderingSettings.enablePreloading = true;

          this.adsManager = evt.getAdsManager(contentVideoSlot, adsRenderingSettings);

          if (this.adsManager) {
            const GoogleImaAdEvents = this.controller.AdEvent;
            const GoogleImaErrorEvents = this.controller.AdErrorEvent;

            this.adsManager.addEventListener(GoogleImaErrorEvents.Type.AD_ERROR, (error) => {
              console.log('error 2: ', error);
              this.localEmitter.emit(event.Ad.AdError);
              this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.LOADED, (e) => {
              const ad = e.getAd();

              // if (!ad.isLinear()) {
              //   console.log('non-linear ad error');
              //   return this.localEmitter.emit(event.Ad.AdError);
              // }

              this.localEmitter.emit(event.Ad.AdLoaded);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.IMPRESSION, () => {
              this.localEmitter.emit(event.Ad.AdImpression);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.STARTED, () => {
              this.localEmitter.emit(event.Ad.AdVideoStart);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.FIRST_QUARTILE, () => {
              this.localEmitter.emit(event.Ad.AdVideoFirstQuartile);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.MIDPOINT, () => {
              this.localEmitter.emit(event.Ad.AdVideoMidpoint);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.THIRD_QUARTILE, () => {
              this.localEmitter.emit(event.Ad.AdVideoThirdQuartile);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.COMPLETE, () => {
              this.localEmitter.emit(event.Ad.AdVideoComplete);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.ALL_ADS_COMPLETED, () => {
              // this.localEmitter.emit(event.Player.AdUnitFinished);
              this.localEmitter.emit(event.Ad.AllAdComplete);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.SKIPPED, () => {
              this.localEmitter.emit(event.Ad.AdVideoComplete);
              // this.localEmitter.emit(event.Ad.AdSkipped);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.VOLUME_CHANGED, () => {
              // if (!userClickedOnSoundButton && this.adsManager.getVolume() > 0)
              //   this.adsManager.setVolume(0);
              this.localEmitter.emit(event.Ad.AdVolumeChange);
            });

            this.adsManager.addEventListener(GoogleImaAdEvents.Type.CLICK, () => {
              this.localEmitter.emit(event.Ad.AdClickThru);

              setTimeout(() => {
                if (this.adsManager) {
                  this.adsManager.resume();
                }
              }, 1000);
            });

            // this.adsManager.addEventListener(GoogleImaAdEvents.Type.CONTENT_PAUSE_REQUESTED, () => {
            //   emitter.emit(event.Ad.AdPaused, { advID });
            // });

            // this.adsManager.addEventListener(GoogleImaAdEvents.Type.CONTENT_RESUME_REQUESTED, () => {
            //   emitter.emit(event.Ad.AdPlaying, { advID });
            // });
          }

          this.videoSlot.load();
          this.adsManager.setVolume(0);

          this.emitter.emit(event.Player.AdPreloaded, this);
          this.status = AdUnitStatus.LOADED;
        }
      );
    }

    const adsRequest = new this.controller.AdsRequest();
    adsRequest.adTagUrl = this.config.tagUrl; // 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/vmap_ad_samples&sz=640x480&cust_params=sar%3Da0f2&ciu_szs=300x250&ad_rule=1&gdfp_req=1&output=vmap&unviewed_position_start=1&env=vp&impl=s&correlator='; 
    adsRequest.linearAdSlotWidth = this.slot.clientWidth;
    adsRequest.linearAdSlotHeight = this.slot.clientHeight;
    adsRequest.nonLinearAdSlotWidth = this.slot.clientWidth;
    adsRequest.nonLinearAdSlotHeight = this.slot.clientHeight;

    adsRequest.setAdWillAutoPlay(true);
    adsRequest.setAdWillPlayMuted(true);

    this.adsLoader.requestAds(adsRequest);
  }

  start = () => {
    this.logger.log(`[GoogleIMA - ${this.config.playerConfig.zone} / ${this.config.params.tid}] start() called`, this);

    this.slot.classList.add("active");

    this.timers.startAd = setTimeout(() => {
      this.logger.log(`[GoogleIMA - ${this.config.playerConfig.zone} / ${this.config.params.tid}] startAd timeout`);
      this.localEmitter.emit(event.Ad.AdError, { code: 2 });
      // this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });
    }, this.config.timeouts.googlestart || 5000);

    const width = this.videoSlot.clientWidth;
    const height = this.videoSlot.clientHeight;

    // this.adDisplayContainer.initialize();
    this.adsManager.init(width, height, this.controller.ViewMode.NORMAL);
    this.adsManager.resize(
      this.slot.parentElement.clientWidth,
      this.slot.parentElement.clientHeight,
      this.controller.ViewMode.NORMAL
    );

    this.adsManager.start();
  }

  pause = () => {
    this.adsManager.pause && this.adsManager.pause();
  }
  resume = () => {
    this.adsManager.resume && this.adsManager.resume();
  }
  resize = (width, height) => {
    this.adsManager && this.adsManager.resize(width, height, this.controller.ViewMode.NORMAL);
  }
  getDuration = () => {
    try {
      return this.adsManager && this.adsManager.getCurrentAd().getDuration();
    } catch (e) {}
  }
  getCurrentTime = () => {
    try {
      return this.adsManager && this.adsManager.getCurrentAd().getDuration() - this.adsManager.getRemainingTime();
    } catch (e) {}
  }
  getVolume = () => {
    return this.adsManager.getVolume();
  }
  setVolume = (volume) => {
    return this.adsManager.setVolume(volume);
  }
}

export default GoogleIMA;
